import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { ArrowLeft, Clock, Calendar, Eye, Share2, Facebook, Twitter, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import type { BlogPost } from "@shared/schema";
import { useEffect } from "react";
import { buildArticleSchema } from "@/lib/seoSchema";

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let tag = document.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }
  Object.entries(attrs).forEach(([key, value]) => tag.setAttribute(key, value));
}

function upsertJsonLd(id: string, data: unknown) {
  let tag = document.getElementById(id) as HTMLScriptElement | null;
  if (!tag) {
    tag = document.createElement("script");
    tag.id = id;
    tag.type = "application/ld+json";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
}

export default function BlogPostPage() {
  const [match, params] = useRoute("/blogs/:slug");
  const slug = params?.slug;

  const { data: blogPost, isLoading } = useQuery<BlogPost>({
    queryKey: [slug ? `/api/blog-posts/${slug}` : ''],
    enabled: !!slug,
  });

  useEffect(() => {
    if (blogPost) {
      const title = blogPost.metaTitle || `${blogPost.title} | Variable Soft`;
      const description = blogPost.metaDescription || blogPost.excerpt;
      const image = blogPost.ogImage || blogPost.imageUrl;
      const canonical = `https://variablesoft.com/blogs/${blogPost.slug}`;

      document.title = title;

      upsertMeta('meta[name="description"]', { name: "description", content: description });

      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        canonicalLink.setAttribute("href", canonical);
      }

      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', title);
      }

      const ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription) {
        ogDescription.setAttribute('content', description);
      }

      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.setAttribute('content', canonical);
      }

      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) {
        ogImage.setAttribute('content', image);
      }

      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (twitterTitle) {
        twitterTitle.setAttribute('content', title);
      }

      const twitterDescription = document.querySelector('meta[name="twitter:description"]');
      if (twitterDescription) {
        twitterDescription.setAttribute('content', description);
      }

      const twitterUrl = document.querySelector('meta[name="twitter:url"]');
      if (twitterUrl) {
        twitterUrl.setAttribute('content', canonical);
      }

      const twitterImage = document.querySelector('meta[name="twitter:image"]');
      if (twitterImage) {
        twitterImage.setAttribute('content', image);
      }
      upsertJsonLd(
        "variablesoft-jsonld",
        buildArticleSchema({
          title,
          headline: blogPost.title,
          description,
          canonical,
          pathname: `/blogs/${blogPost.slug}`,
          image,
          author: blogPost.author,
          datePublished: blogPost.publishedAt,
          dateModified: blogPost.updatedAt || blogPost.publishedAt,
        })
      );
    }
  }, [blogPost]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Navigation />
        <div className="pt-32 pb-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="animate-pulse space-y-6">
              <div className="h-4 w-24 bg-gray-200 dark:bg-gray-800 rounded"></div>
              <div className="h-10 bg-gray-200 dark:bg-gray-800 rounded"></div>
              <div className="h-72 bg-gray-200 dark:bg-gray-800 rounded"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
                <div className="h-4 w-5/6 bg-gray-200 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!blogPost) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Navigation />
        <div className="pt-32 pb-20 text-center">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">Blog Post Not Found</h1>
            <p className="text-gray-600 dark:text-gray-300 mb-8">The blog post you're looking for doesn't exist.</p>
            <Button asChild>
              <a href="/blog">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </a>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const tags = blogPost.tags ? JSON.parse(blogPost.tags) : [];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navigation />
      
      <article className="pt-28 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Back button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-8"
          >
            <Button 
              variant="ghost" 
              size="sm"
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              asChild
            >
              <a href="/blog">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </a>
            </Button>
          </motion.div>

          {/* Article header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Badge 
              variant="secondary"
              className="mb-4 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
            >
              {blogPost.category}
            </Badge>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
              {blogPost.title}
            </h1>
            
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              {blogPost.excerpt}
            </p>

            {/* Article meta */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 dark:text-gray-400 mb-8">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <img
                    src={blogPost.authorAvatar}
                    alt={blogPost.author}
                    className="w-8 h-8 rounded-full"
                  />
                  <span className="font-medium text-gray-900 dark:text-white">{blogPost.author}</span>
                </div>
                
                <div className="h-4 w-px bg-gray-300 dark:bg-gray-700"></div>
                
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(blogPost.publishedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}</span>
                </div>
                
                <div className="h-4 w-px bg-gray-300 dark:bg-gray-700"></div>
                
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{blogPost.readTime} min read</span>
                </div>
                
                <div className="h-4 w-px bg-gray-300 dark:bg-gray-700"></div>
                
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  <span>{(blogPost.views || 0).toLocaleString()} views</span>
                </div>
              </div>
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {tags.map((tag: string, index: number) => (
                  <Badge 
                    key={index} 
                    variant="outline"
                    className="text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-700"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </motion.header>

          {/* Featured image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-10"
          >
            <img
              src={blogPost.imageUrl}
              alt={blogPost.title}
              className="w-full h-64 md:h-80 object-cover rounded-xl"
            />
          </motion.div>

          {/* Article content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-12"
          >
            <div 
              className="text-gray-700 dark:text-gray-300 leading-relaxed space-y-4 text-base md:text-lg"
              dangerouslySetInnerHTML={{ __html: blogPost.content }}
            />
          </motion.div>

          <Separator className="mb-8" />

          {/* Share section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-between gap-4"
          >
            <div className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              <span className="font-medium text-gray-900 dark:text-white">Share this article:</span>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                className="h-9 w-9 p-0"
                asChild
              >
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(blogPost.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Twitter className="h-4 w-4" />
                </a>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="h-9 w-9 p-0"
                asChild
              >
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="h-9 w-9 p-0"
                asChild
              >
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
